import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clientService from '../services/clientService';
import { useNotification } from '../context/NotificationContext';
import { AuthContext } from '../context/AuthContext';
import '../styles/general.css';
import '../styles/clients.css';

const ClientsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { canManage } = useContext(AuthContext);
  // Write actions are hidden, not disabled: a button the user can never
  // enable is its own kind of noise. The API rejects them regardless.
  const canWrite = canManage && canManage('clients');

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('-created_at');
  const [filterField, setFilterField] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const perPage = 50;
  const totalPages = Math.ceil(totalCount / perPage);

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const res = await clientService.listClients(
        filterField || null,
        filterValue || null,
        searchValue || null,
        sortBy,
        page,
        showInactive
      );
      setClients(res.data.results || []);
      setTotalCount(res.data.count || 0);
    } catch (err) {
      console.error('Error loading clients:', err);
      notify.error(t('common.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, filterField, filterValue, searchValue, showInactive, notify, t]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const handleToggleActive = (id, currentStatus) => {
    clientService.toggleActive(id)
      .then(() => {
        const message = currentStatus ? t('clients.clientDeactivated') : t('clients.clientActivated');
        notify.success(message);
        loadClients();
      })
      .catch(err => {
        notify.error(t('common.failedToLoad'));
        console.error(err);
      });
  };

  const handleFilterChange = (field, value) => {
    setFilterField(field);
    setFilterValue(value);
    setPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setPage(1);
  };

  const handleShowInactiveChange = (checked) => {
    setShowInactive(checked);
    setPage(1);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortBy(`-${field}`);
    } else if (sortBy === `-${field}`) {
      setSortBy(field);
    } else {
      setSortBy(`-${field}`);
    }
    setPage(1);
  };

  if (loading && clients.length === 0) {
    return (
      <div className="loading">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="page-container-wide">
      <div className="page-header">
        <h1>{t('clients.title')}</h1>
        {canWrite && (
          <button
            onClick={() => navigate('/clients/create')}
            className="btn btn-primary"
          >
            {t('clients.createButton')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="filter-panel">
        <div className="filter-row">
          <div className="filter-group">
            <label className="form-label">
              {t('filters.filterBy')}
            </label>
            <select
              value={filterField}
              onChange={(e) => handleFilterChange(e.target.value, filterValue)}
              className="form-select"
            >
              <option value="">{t('filters.none')}</option>
              <option value="name">{t('clients.filters.byName')}</option>
              <option value="tax_id">{t('clients.filters.byTaxId')}</option>
              <option value="phone">{t('clients.filters.byPhone')}</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="form-label">
              {t('filters.searchValue')}
            </label>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t('filters.searchValue')}
              className="form-input"
            />
          </div>

          <div className="filter-group">
            <label className="form-label">
              {t('filters.sortBy')}
            </label>
            <select
              value={sortBy}
              onChange={(e) => {
                const val = e.target.value;
                handleSortChange(val.replace('-', ''));
              }}
              className="form-select"
            >
              <option value="-created_at">{t('filters.createdNewest')}</option>
              <option value="created_at">{t('filters.createdOldest')}</option>
              <option value="name">{t('clients.filters.nameAZ')}</option>
              <option value="-name">{t('clients.filters.nameZA')}</option>
              <option value="tax_id">{t('clients.filters.taxIdAZ')}</option>
              <option value="-tax_id">{t('clients.filters.taxIdZA')}</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="form-label">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => handleShowInactiveChange(e.target.checked)}
              />
              {' '}{t('filters.showInactive')}
            </label>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr>
              <th>{t('clients.name')}</th>
              <th>{t('clients.taxId')}</th>
              <th>{t('clients.phone')}</th>
              <th>{t('clients.contacts')}</th>
              <th>{t('common.status')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  <p>{t('clients.noClients')}</p>
                </td>
              </tr>
            ) : (
              clients.map((client) => (
                <tr key={client.id}>
                  <td>{client.name}</td>
                  <td>{client.tax_id}</td>
                  <td>{client.phone}</td>
                  <td>
                    {client.contact_names ? (
                      <span>{client.contact_names}</span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    <span className={`table-status ${client.is_active ? 'active' : 'inactive'}`}>
                      {client.is_active ? t('clients.active') : t('clients.inactive')}
                    </span>
                  </td>
                  <td className="table-actions">
                    {canWrite && (
                      <button
                        onClick={() => navigate(`/clients/${client.id}/edit`)}
                        className="btn btn-primary btn-small"
                      >
                        {t('common.edit')}
                      </button>
                    )}
                    <button
                      onClick={() => handleToggleActive(client.id, client.is_active)}
                      className={`btn btn-small ${client.is_active ? 'btn-danger' : 'btn-info'}`}
                    >
                      {client.is_active ? t('clients.deactivateButton') : t('clients.activateButton')}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="btn btn-primary"
          >
            {t('common.previous')}
          </button>
          <span className="pagination-info">
            {t('common.page')} {page}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="btn btn-primary"
          >
            {t('common.next')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ClientsListPage;
